import { getAuthHeaders } from '@/identity.js';

/**
 * Why a file born in a chat stays when the chat is deleted. `shared` and `used_in_other_chat` both
 * mean its audience reaches past the chat; the other two mean it is not the caller's to remove.
 */
export type ChatFileKeptReason =
  'shared' | 'used_in_other_chat' | 'created_by_someone_else' | 'not_allowed';

/** One file born in a chat, as the delete dialog shows it. */
export interface ChatCreatedFile {
  artifactId: string;
  name: string;
  deletable: boolean;
  keptReason: ChatFileKeptReason | null;
}

const KEPT_REASONS: ReadonlySet<string> = new Set<ChatFileKeptReason>([
  'shared',
  'used_in_other_chat',
  'created_by_someone_else',
  'not_allowed',
]);

/**
 * The files born in a chat, each marked whether its author may delete them together with the chat
 * (`GET /v1/conversations/:id/created-files`, MEDA-1150). Author-only on the server; throws on any
 * non-2xx so the dialog can say it could not check, instead of reading "no files".
 */
export async function fetchChatCreatedFiles(
  conversationId: string,
): Promise<ChatCreatedFile[]> {
  const res = await fetch(
    `/v1/conversations/${encodeURIComponent(conversationId)}/created-files`,
    { headers: await getAuthHeaders() },
  );
  if (!res.ok) throw new Error(`Failed to list chat files: ${res.status}`);
  const body = (await res.json()) as { files?: unknown };
  return Array.isArray(body.files) ? body.files.map(toChatCreatedFile) : [];
}

/**
 * One wire row (snake_case) in its client shape. A row is offered only on an explicit `deletable: true`;
 * an unknown kept reason still keeps the file, read as `shared`, the reason that claims the least.
 */
function toChatCreatedFile(row: unknown): ChatCreatedFile {
  const record = (row ?? {}) as Record<string, unknown>;
  const deletable = record.deletable === true;
  const reason = String(record.kept_reason ?? '');
  return {
    artifactId: String(record.artifact_id ?? ''),
    name: String(record.name ?? ''),
    deletable,
    keptReason: deletable
      ? null
      : KEPT_REASONS.has(reason)
        ? (reason as ChatFileKeptReason)
        : 'shared',
  };
}
